import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@adaptcx/shared/stores/auth'
import { useAssistConfigStore } from '@/stores/assistConfig'

// Role shortcuts for meta.roles
const ADMIN = ['assist_admin']
const ADMIN_SUP = ['assist_admin', 'assist_supervisor']
const ALL_ROLES = ['assist_admin', 'assist_supervisor', 'assist_agent']

const routes = [
  // Top-level auth routes (no layout wrapper)
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/sso/callback',
    name: 'SSOCallback',
    component: () => import('@adaptcx/shared').then(m => m.SSOCallback),
    meta: { requiresAuth: false },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { roles: ALL_ROLES },
      },
      {
        path: 'profile',
        name: 'profile-settings',
        component: () => import('@/views/profile/ProfileSettingsView.vue'),
        meta: { roles: ALL_ROLES },
      },
      // Setup
      {
        path: 'setup',
        name: 'setup',
        component: () => import('@/views/setup/SetupWizardView.vue'),
        meta: { roles: ADMIN },
      },
      // Conversations
      {
        path: 'conversations/live',
        name: 'live-conversations',
        component: () => import('@/views/conversations/LiveConversationsView.vue'),
        meta: { roles: ALL_ROLES },
      },
      {
        path: 'conversations/history',
        name: 'conversation-history',
        component: () => import('@/views/conversations/ConversationHistoryView.vue'),
        meta: { roles: ALL_ROLES },
      },
      // Knowledge & Search
      {
        path: 'knowledge/rag-performance',
        name: 'rag-performance',
        component: () => import('@/views/knowledge/RagPerformanceView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'knowledge/gap-analysis',
        name: 'gap-analysis',
        component: () => import('@/views/knowledge/GapAnalysisView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'knowledge/prompt-lab',
        name: 'prompt-lab',
        component: () => import('@/views/knowledge/PromptLabView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      // Pub/Sub
      {
        path: 'pubsub/topics',
        name: 'pubsub-topics',
        component: () => import('@/views/pubsub/TopicManagementView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'pubsub/health',
        name: 'pubsub-health',
        component: () => import('@/views/pubsub/HealthMonitorView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'pubsub/inspector',
        name: 'event-inspector',
        component: () => import('@/views/pubsub/EventInspectorView.vue'),
        meta: { roles: ADMIN },
      },
      // AudioHook
      {
        path: 'audiohook/streams',
        name: 'active-streams',
        component: () => import('@/views/audiohook/ActiveStreamsView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'audiohook/recordings',
        name: 'recordings',
        component: () => import('@/views/audiohook/RecordingsView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      // Middleware
      {
        path: 'middleware/connections',
        name: 'connections',
        component: () => import('@/views/middleware/ConnectionsView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'middleware/queues',
        name: 'queue-mapping',
        component: () => import('@/views/middleware/QueueMappingView.vue'),
        meta: { roles: ADMIN },
      },
      // Analytics
      {
        path: 'analytics/conversation-insights',
        name: 'conversation-insights',
        component: () => import('@/views/analytics/ConversationInsightsView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'analytics/efficiency',
        name: 'agent-efficiency',
        component: () => import('@/views/analytics/AgentEfficiencyView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'analytics/classifier',
        name: 'classifier-analytics',
        component: () => import('@/views/analytics/ClassifierAnalyticsView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'analytics/cost',
        name: 'cost-usage',
        component: () => import('@/views/analytics/CostUsageView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'analytics/feedback',
        name: 'feedback-analytics',
        component: () => import('@/views/analytics/FeedbackAnalyticsView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'analytics/source-feedback',
        name: 'source-feedback-review',
        component: () => import('@/views/analytics/SourceFeedbackReviewView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      {
        path: 'analytics/dlp',
        name: 'dlp-analytics',
        component: () => import('@/views/analytics/DlpAnalyticsView.vue'),
        meta: { roles: ADMIN },
      },
      // Coaching — Phase 2.30 v2.4.7: playbook editor uses VA's
      // ops-portal Vue components (PlaybooksListView +
      // PlaybookEditorView, copied into views/intelligence/) but
      // mounted at assist-native internal paths under
      // `/coaching/...`. Keeping the route namespace distinct from
      // VA's `/intelligence/...` avoids cross-portal coupling: if
      // either portal evolves its internal paths, the other isn't
      // affected.
      {
        path: 'coaching/playbooks',
        name: 'playbooks-list',
        component: () => import('@/views/intelligence/PlaybooksListView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'coaching/playbooks/new',
        name: 'playbook-new',
        component: () => import('@/views/intelligence/PlaybookEditorView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'coaching/playbooks/:id/edit',
        name: 'playbook-edit',
        component: () => import('@/views/intelligence/PlaybookEditorView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'coaching/queue-assignments',
        name: 'queue-assignments',
        component: () => import('@/views/coaching/QueueAssignmentView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'analytics/coaching',
        name: 'coaching-analytics',
        component: () => import('@/views/analytics/CoachingAnalyticsView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      // Tools
      {
        path: 'tools/classifier-test',
        name: 'classifier-test',
        component: () => import('@/views/tools/ClassifierTestView.vue'),
        meta: { roles: ADMIN },
      },
      // CCAI Console (google_native mode only)
      {
        path: 'ccai/profiles',
        name: 'ccai-profiles',
        component: () => import('@/views/ccai/ConversationProfilesView.vue'),
        meta: { roles: ADMIN, mode: 'google_native' },
      },
      {
        path: 'ccai/profiles/:profileName',
        name: 'ccai-profile-editor',
        component: () => import('@/views/ccai/ProfileEditorView.vue'),
        meta: { roles: ADMIN, mode: 'google_native' },
      },
      // CCAI Knowledge Bases removed — deprecated by Google (April 2026).
      // Data stores are now managed in Operations Portal → Vertex AI Search.
      {
        path: 'ccai/models',
        name: 'ccai-models',
        component: () => import('@/views/ccai/ModelsView.vue'),
        meta: { roles: ADMIN, mode: 'google_native' },
      },
      {
        path: 'ccai/test-bench',
        name: 'ccai-test-bench',
        component: () => import('@/views/ccai/TestBenchView.vue'),
        meta: { roles: ADMIN, mode: 'google_native' },
      },
      // Settings
      {
        path: 'settings',
        name: 'assist-config',
        component: () => import('@/views/settings/AssistConfigView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'settings/domain-keywords',
        name: 'domain-keywords',
        component: () => import('@/views/settings/DomainKeywordsView.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'settings/health',
        name: 'service-health',
        component: () => import('@/views/settings/ServiceHealthView.vue'),
        meta: { roles: ADMIN },
      },
      // v1.3.0: per-tenant agent allowlist for audiohook init gating.
      // View read-only for supervisors; admin required to modify.
      {
        path: 'settings/agent-allowlist',
        name: 'agent-allowlist',
        component: () => import('@/views/settings/AgentAllowlistView.vue'),
        meta: { roles: ADMIN_SUP },
      },
      // v1.3.4: PII Audit dashboard. Admin-only — PII content visible
      // in sample snippets.
      {
        path: 'settings/pii-audit',
        name: 'pii-audit',
        component: () => import('@/views/settings/PiiAuditView.vue'),
        meta: { roles: ADMIN },
      },
      // v2.4.54: Cisco WxCC integration simulator. Lets a tenant admin
      // stream a recorded WAV through the audiohook as if Webex
      // Contact Center were forking live audio — end-to-end validation
      // before Cisco-side provisioning is finalized. View itself
      // gates visibility on widget_provider === 'cisco_wxcc'.
      {
        path: 'settings/cisco-simulator',
        name: 'cisco-simulator',
        component: () => import('@/views/settings/CiscoSimulatorView.vue'),
        meta: { roles: ADMIN },
      },
      // v2.5.99: Cisco Finesse (UCCE) CTI simulator. Fires a synthetic dialog
      // through the real correlation path (no Finesse server). View gates
      // itself on the simulator being enabled server-side.
      {
        path: 'settings/finesse-simulator',
        name: 'finesse-simulator',
        component: () => import('@/views/settings/FinesseSimulatorView.vue'),
        meta: { roles: ADMIN },
      },
    ],
  },
  // Catch all - redirect to dashboard or login
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Platform / elevated user types that bypass assist role checks. MUST stay a
// superset of the login gate's allowedTypes (LoginView accessCheck) — if a user
// can sign in but cannot pass this, the guard loops dashboard->dashboard.
const PLATFORM_TYPES = ['super_admin', 'platform_admin', 'partner_admin', 'tenant_admin', 'operations', 'supervisor']

// Broad assist grants that admit any assist route (mirror LoginView accessCheck).
const ASSIST_WILDCARD_PERMS = ['*', 'agent_assist.manage', 'agent_assist.admin']

// Map role names to a distinguishing permission code in the JWT
const ROLE_MARKER_PERMISSIONS: Record<string, string> = {
  assist_admin: 'assist:config:write',
  assist_supervisor: 'assist:analytics:read',
  assist_agent: 'assist:conversations:read',
}

function hasAssistRole(user: any, allowedRoles: string[]): boolean {
  if (!user) return false
  if (PLATFORM_TYPES.includes(user.user_type)) return true
  const perms: string[] = user.permissions ?? []
  if (perms.some(p => ASSIST_WILDCARD_PERMS.includes(p))) return true
  return allowedRoles.some(role => {
    // Check marker permission for the role
    const marker = ROLE_MARKER_PERMISSIONS[role]
    if (marker && perms.includes(marker)) return true
    // Also accept the role code directly (future-proof)
    return perms.includes(role)
  })
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if (to.meta.requiresAuth === false && authStore.isAuthenticated && to.name !== 'SSOCallback') {
    next({ name: 'dashboard' })
  } else if (to.meta.roles && !hasAssistRole(authStore.user, to.meta.roles as string[])) {
    // Redirect unauthorized roles to dashboard (agents will see conversations there).
    // Never redirect dashboard->dashboard: it is itself role-gated, so that loops.
    if (to.name === 'dashboard') next()
    else next({ name: 'dashboard' })
  } else if (to.meta.mode) {
    // Mode-gated routes (e.g., CCAI Console only for google_native)
    const configStore = useAssistConfigStore()
    if (configStore.assistMode !== to.meta.mode && to.name !== 'dashboard') {
      next({ name: 'dashboard' })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
